import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as utils from '@/utils';
/** The CodeGrade Icon component. */
export const Icon = tsx
    .componentFactoryOf()
    .create({
    name: 'icon',
    props: {
        icon: p.ofType().required,
        title: p(String).optional,
        scale: p(Number).optional,
        tabindex: p(Number).optional,
        spin: p(Boolean).default(false),
        filled: p(Boolean).default(false),
    },
    /** @returns The init state of the component. */
    data() {
        return { hasErrored: false };
    },
    /** The Vue lifecycle hook errorCaptured, used to handle errors from within the component. */
    errorCaptured(err, vm, info) {
        if (!IS_PRODUCTION) {
            /* eslint-disable no-console */
            console.error('Icon component has captured an error!');
            console.error(err, vm, info);
            console.trace();
            /* eslint-enable no-console */
            this.hasErrored = true;
        }
    },
    computed: {
        /** @returns The title tag for the SVG node. */
        svgTitleNode() {
            if (this.title == null) {
                return utils.emptyVNode();
            }
            return <title>{this.title}</title>;
        },
        /** @returns The Lucide IconNode being rendered as svg nodes. */
        svgContentNode() {
            if (this.icon == null) {
                return [utils.emptyVNode()];
            }
            return this.icon.map(([Tag, attrs]) => (
            // @ts-expect-error TypeScript does not understand tsx-vue-support's style of prop spreading.
            <Tag {...{ attrs }}/>));
        },
        /** @returns The nodes to rendered after the icon component has captured any error. */
        errorNode() {
            if (IS_PRODUCTION) {
                return utils.emptyVNode();
            }
            return (<div class="icon bg-critical-40! text-white! text-xs size-6 p-px whitespace-pre" style={this.scaleStyle}>
                        !ICON
                    </div>);
        },
        /** @returns The style object concerning the scaling of the icon. */
        scaleStyle() {
            if (this.scale == null) {
                return {};
            }
            return {
                fontSize: `${this.scale}em`,
            };
        },
    },
    /** @returns The nodes of the component. */
    render() {
        const style = { ...this.scaleStyle };
        if (this.hasErrored || this.icon == null) {
            return this.errorNode;
        }
        return (<svg role="img" tabindex={this.tabindex} class={['icon', { 'animate-spin': this.spin, 'fill-current': this.filled }]} viewBox="0 0 24 24" style={style} onClick={(evt) => tsx.emitOn(this, 'onClick', evt)} aria-hidden focusable="false">
                    {this.svgTitleNode}
                    {this.svgContentNode}
                </svg>);
    },
});
/**
 * A helper function to make icons easily available in a Vue component.
 *
 * @example
 * ```vue
 * <script>
 * import { IconA, IconB, IconC } from "@codegrade/icons";
 * import { Icon, mapIcons } from "@/components/Icon"
 *
 * export default {
 *   computed: {
 *     ...mapIcons({ IconA, IconB, IconC })
 *   }
 * }
 * </script>
 * <template>
 *   <div>
 *     <Icon :icon="icons.IconA" />
 *     <Icon :icon="icons.IconB" />
 *     <Icon :icon="icons.IconC" />
 *   </div>
 * </template>
 * ```
 *
 * @param record A set of icons to make available in the Vue component.
 * @returns An object with the computed property to be spread onto the component.
 */
export function mapIcons(record) {
    return {
        icons: () => record,
    };
}
